<template>
  <div class="grid-container m-auto w-full max-w-[2500px]">
    <div
      v-if="!isMobileOrTablet || images.length <= 1"
      class="stage-image relative mb-24 flex-col justify-center bg-grey-light-01 lg:flex"
      :class="{
        flex: images.length <= 1,
        hidden: images.length > 1,
      }"
    >
      <StageMainMedia
        :active-image-index="activeImageIndex"
        :images="images"
        :variation="variation"
      />
    </div>
    <ResponsiveSlider
      v-if="isMobileOrTablet && (images?.length ?? 0) > 1"
      class="stage-slider static mb-24 h-[50vh] bg-grey-light-01 lg:hidden"
      :images="images"
      :active="activeImageIndex"
      :mobile="true"
      @set-active="setActiveIndex"
    />
    <div
      class="col-span-6 col-start-1 ml-16 flex flex-col justify-end pb-[22px] lg:col-span-7 lg:col-start-8"
    >
      <div class="sticky bottom-[22px] h-fit">
        <div class="custom-mr">
          <div class="mb-24">
            <Text
              html-element-type="h2"
              class="pdp-stage-tagline text-base uppercase text-grey-dark-01"
            >
              {{ tagline }}
            </Text>
            <div class="stage-heading-wrapper pt-8 lg:pt-16">
              <Text
                html-element-type="h1"
                class="pdp-stage-title text-3xl uppercase lg:text-5xl"
                :class="{
                  'font-bold': isRebranding,
                }"
              >
                <!-- Do not split titles at dashes -->
                {{ variation.label?.replaceAll('-', '&#x2011;') }}
              </Text>
              <CommercePrice
                v-if="variation.price"
                class="stage-price text-2xl"
                :price="variation.price"
              />
            </div>
          </div>
          <div class="stage-details mb-32">
            <dl
              class="col-span-12 grid grid-cols-[max-content,1fr] gap-x-16 self-end"
            >
              <template v-for="item in technicalDetails">
                <dt>{{ item.label }}</dt>
                <dd>{{ item.value }}</dd>
              </template>
            </dl>
            <!-- size selector for love Items -->
            <InputDropdown
              v-if="availableSizes && availableSizes?.length > 1"
              v-model="selectedSize"
              class="col-span-12 mb-32 mt-36"
              :items="availableSizes"
              width-classes="w-fit min-w-[170px]"
            />
          </div>
        </div>

        <ResponsiveSlider
          v-if="!isMobileOrTablet && images.length > 1"
          class="stage-slider mb-24 hidden w-full lg:block"
          :images="images"
          :active="activeImageIndex"
          @set-active="setActiveIndex"
        />

        <div class="custom-mr">
          <template v-if="ctaButtonData">
            <p class="stage-button-info mb-30 text-center">
              {{ ctaButtonData.text }}
            </p>
            <OrisButton
              color="primary"
              class="stage-button"
              :icon-end="ctaButtonData.icon"
              @click="ctaButtonData?.handleClick"
              >{{ ctaButtonData.label }}</OrisButton
            >
          </template>
          <AddToCart
            v-else-if="ctaButton === CtaButtonType.Order_now && variation.sku"
            :variation="variation"
            class="stage-button"
          />
        </div>

        <!-- Notify modal -->
        <Transition appear name="page-modal">
          <NotifyMe
            v-if="variation.sku && openModal === 'notify_me'"
            :sku="variation.sku"
            @close="openModal = ''"
          />
        </Transition>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import StageMainMedia from './StageMainMedia.vue'
import {
  CtaButtonType,
  type PageProductVariationGiftFragment,
  type PageProductVariationToolFragment,
  type PageProductVariationGiveawayFragment,
  type PageProductVariationWatchFragment,
  type PageProductVariationStrapFragment,
  type ParagraphPdpStageVideoFragment,
  type ParagraphPdpStageImageFragment,
} from '#graphql-operations'
import NotifyMe from '@/components/PDP/NotifyMe/index.vue'
import OrisButton from '@/components/Buttons/Button.vue'
import ResponsiveSlider from '@/components/Slider/ResponsiveSlider.vue'
import type { DropdownItem } from '~/components/Input/Dropdown.vue'
import { falsy } from '~/helpers/type'

const router = useRouter()
const route = useRoute()
const isRebranding = await useFeatureFlag()

// Use this so we can correctly unmound the mobile and desktop sliders when they are not needed. This way we also get rid of the videos.
const { isMobileOrTablet } = useViewport()

// The currently open modal.
const openModal = useQueryString('modal', '', {
  replace: true,
})
const { $texts } = useNuxtApp()

const props = defineProps<{
  tagline?: string
  ctaButton?: CtaButtonType
  availableSizes?: DropdownItem[]
  variation:
    | PageProductVariationGiftFragment
    | PageProductVariationGiveawayFragment
    | PageProductVariationToolFragment
    | PageProductVariationWatchFragment
    | PageProductVariationStrapFragment
}>()

const isMediaItem = (
  media: object,
): media is ParagraphPdpStageVideoFragment | ParagraphPdpStageImageFragment => {
  if (media && '__typename' in media) {
    return (
      media.__typename === 'ParagraphPdpStageVideo' ||
      media.__typename === 'ParagraphPdpStageImage'
    )
  }
  return false
}

const images = computed(() => {
  if ('stageMedia' in props.variation) {
    // `stageMedia` is a union type of ALL Paragrph types,
    // not only the ones allowed in the PDP Stage.
    // Therefore we use `filter` to refine the type.
    return props.variation.stageMedia?.filter(isMediaItem) ?? []
  } else {
    return []
  }
})

const activeImageIndex = ref<number & keyof typeof images.value>(0)

const setActiveIndex = (newIndex: number) => {
  activeImageIndex.value = newIndex
}

const ctaButtonProps: Partial<
  Record<
    CtaButtonType,
    { label: string; text?: string; icon: string; handleClick: () => void }
  >
> = {
  [CtaButtonType.Find_a_store]: {
    label: $texts('pdp.ctaFindAStoreLabel', 'Find a store'),
    text: $texts(
      'pdp.ctaFindAStoreText',
      'Sorry, this model is archived and no longer on sale. Try in one of our stores.',
    ),
    icon: 'store',
    handleClick: () => {
      router.push({ name: 'storefinder' })
    },
  },
  [CtaButtonType.Notify_me]: {
    label: $texts('pdp.ctaNotifyMeLabel', 'Notify me'),
    text: $texts(
      'pdp.ctaNotifyMeText',
      'Sorry, this model is sold out. Let us inform you as soon as it is available again.',
    ),
    icon: 'bell',
    handleClick: () => {
      openModal.value = 'notify_me'
    },
  },
}

const ctaButtonData = computed(() =>
  props.ctaButton ? ctaButtonProps[props.ctaButton] : undefined,
)

const technicalDetails = computed(() => {
  const variation = props.variation

  let technicalDetailsArray: {
    label: string
    value: string | undefined | unknown
  }[] = [{ label: $texts('Reference', 'Reference'), value: variation.sku }]

  if (variation.__typename === 'CommerceProductVariationWatchDefault') {
    technicalDetailsArray = technicalDetailsArray.concat([
      {
        label: $texts('dialSize', 'Size'),
        value: variation.fieldCaseSize,
      },
      {
        label: $texts('Material', 'Material'),
        value: variation.fieldCaseMaterial,
      },
    ])
  } else if (variation.__typename === 'CommerceProductVariationStrapDefault') {
    technicalDetailsArray = technicalDetailsArray.concat([
      { label: $texts('Size', 'Size'), value: variation.interhornWidth },
      {
        label: $texts('Material', 'Material'),
        value: variation.materialGroup,
      },
      { label: $texts('Color', 'Color'), value: variation.color },
      { label: $texts('Locker', 'Locker'), value: variation.buckle },
      { label: $texts('Extra', 'Extra'), value: variation.extras },
    ])
  }

  return technicalDetailsArray.filter(
    (item): item is { label: string; value: string } => falsy(item.value),
  )
})

const selectedSize = computed({
  get() {
    return route.path
  },
  set(v: string) {
    router.push(v)
  },
})
</script>

<style lang="postcss" scoped>
.grid-container {
  display: grid;
  grid-template-columns: 1fr repeat(4, min(75px, 18vw)) 1fr;
  align-content: end;
  column-gap: min(15px, 4vw);

  @media (screen(lg)) {
    grid-template-columns: 1fr repeat(12, 56px) 1fr;
    column-gap: 24px;
  }
}

.stage-image {
  grid-column: 1 / span 6;
  height: 50vh;

  img {
    height: inherit;
    width: 100%;
  }

  :deep(picture) {
    width: 100%;
    height: 100%;
  }

  .smaller-main-image {
    :deep(picture) {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  @media (screen(lg)) {
    grid-column: 1 / span 7;
    height: calc(100dvh - 46px);
    align-content: end;
    width: 100%;
    overflow: hidden;

    .img-zoom-in {
      height: inherit;
      width: 100%;
      transition: transform 0.4s ease-in-out;

      &:hover {
        transform: scale(1.1);
        transition: transform 0.4s ease-in-out;
      }
    }
  }
}

.custom-mr {
  margin-right: 16px;

  @media (screen(lg)) {
    margin-right: fluid(45px, 71px, theme('screens.lg'), theme('screens.xxl'));
  }
}

.stage-slider {
  grid-column: 1 / span 6;

  @media (screen(lg)) {
    margin-bottom: 40px;
    grid-column: 8 / span 7;
    height: min-content;
  }
}

.stage-heading-wrapper {
  gap: 6px 24px;

  @media (screen(lg)) {
    display: flex;
    flex-flow: row;
    justify-content: space-between;
    align-items: last baseline;
  }
}

.stage-heading {
  @media (screen(lg)) {
    align-items: flex-start;
    justify-content: end;
    grid-column: 8 / span 6;
  }

  .pdp-stage-title {
    flex-basis: min-content;

    @media (screen(lg)) {
      flex-basis: 63%;
    }
  }

  .pdp-stage-tagline {
    margin-top: 24px;

    @media (screen(lg)) {
      margin-top: 0;
    }
  }
}

.stage-price {
  padding-top: 20px;

  @media (screen(lg)) {
    white-space: nowrap;
    margin-inline: 0;
    padding-bottom: 0;
    padding-top: 0;
    line-height: 48px;
  }
}

.stage-details {
  display: grid;
  align-self: flex-end;
  grid-template-columns: max-content 1fr;
  column-gap: 16px;

  dt,
  dd {
    @apply text-sm;
  }

  dt {
    @apply text-grey-dark-01;
  }

  dd {
    padding-left: 1rem;
  }

  dd:not(:last-child) {
    margin-bottom: 16px;
  }

  @media (screen(lg)) {
    grid-column: 8 / span 6;
  }
}
</style>

<style lang="scss" scoped>
/* stylelint-disable declaration-property-value-no-unknown */
$stage-button-max-width: 621px;

.stage-button-info {
  width: fit-content;
  min-width: min($stage-button-max-width, 100%);
}

.stage-button {
  width: 100%;
  max-width: $stage-button-max-width;
  position: sticky;
  bottom: 22px;
}
</style>
